import { useState, useMemo, useEffect, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import KitchenFields from '../components/KitchenFields';
import MusicOnlyToggle from '../components/MusicOnlyToggle';
import { fmt, fmtDate, fmtDateRange, getVenue, calculateBalance } from '../lib/utils';
import { todayKey } from '../lib/dateHelpers';
import TimeSelect from '../components/TimeSelect';
import { useNotify } from '../components/NotifyProvider';
// Restaurant venue rules live in one place — see lib/restaurantConstants.
import {
  RESTAURANT_VENUES,
  RESTAURANT_SITTINGS,
  getSittingsForVenueAndDate,
  getMinCoversForVenue as getMinCoversForRestaurant,
  guessSittingFromTime,
} from '../lib/restaurantConstants';
import { WEDDING_PIPELINE_STAGES, STAGE_LABELS, STAGE_COLOURS, getPipelineStages } from '../lib/sampleData';
import ContractSigning from '../components/ContractSigning';
import { DetailStatusBadge, DetailStatusPanel } from '../components/EventDetailStatus';
import { getDetailSaveBlock, getEventDetailStatus, missingFieldClass, missingCountByTab } from '../lib/eventCompleteness';
import { Search, MapPin, Users, TrendingUp, Calendar, Plus, Trash2, X, ChevronDown, ChevronUp, Save, CreditCard, FileText, Upload, Download, File, Copy, Utensils, Unlink } from 'lucide-react';
import EmailComposer from '../components/EmailComposer';
import EventTasks from '../components/EventTasks';
import EventHistory from '../components/EventHistory';
import WeddingChecklist from '../components/WeddingChecklist';
import { uploadAttachment, deleteAttachment, getAttachmentUrl, fetchVenueBlocksByDates } from '../lib/supabaseService';
// Ad-hoc closures (Christmas, the January off-season, etc.) for the
// event/function-room venues — see lib/venueClosures.
import { getVenueClosureReason } from '../lib/venueClosures';

const EVENT_TYPES = {
  All: null,
  Weddings: 'wedding',
  Meetings: 'corporate',
  Events: 'private_party',
};

const FB_PRESETS = [
  { value: 'morning_tea', label: 'Morning Tea' },
  { value: 'afternoon_tea', label: 'Afternoon Tea' },
  { value: 'tea_coffee', label: 'Tea & Coffee' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'dessert', label: 'Dessert' },
  { value: 'welcome_cocktail', label: 'Welcome Cocktail' },
  { value: 'cocktail_hour', label: 'Cocktail Hour' },
  { value: 'canapes', label: 'Canapés' },
  { value: 'wine_selection', label: 'Wine Selection' },
  { value: 'cheese_board', label: 'Cheese Board' },
  { value: 'sandwiches', label: 'Sandwiches' },
  { value: 'cake', label: 'Cake' },
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'evening_snacks', label: 'Evening Snacks' },
  { value: 'welcome_drinks', label: 'Welcome Drinks' },
  { value: 'evening_reception', label: 'Evening Reception' },
  { value: 'after_party_room', label: 'After Party Room' },
  { value: 'bbq', label: 'BBQ' },
];

function getFBLabel(value) {
  const preset = FB_PRESETS.find(p => p.value === value);
  return preset ? preset.label : value;
}

const EQUIPMENT_OPTIONS = [
  'projector',
  'sound_system',
  'lighting',
  'stage',
  'dance_floor',
  'flipchart',
  'whiteboard',
  'screen',
  'microphone',
  'lectern',
];

const SETUP_STYLES = [
  { value: 'theatre', label: 'Theatre' },
  { value: 'banquet', label: 'Banquet' },
  { value: 'classroom', label: 'Classroom' },
  { value: 'boardroom', label: 'Boardroom' },
  { value: 'u_shape', label: 'U-Shape' },
  { value: 'cabaret', label: 'Cabaret' },
  { value: 'reception', label: 'Reception' },
  { value: 'other', label: 'Other' },
];

const EVENT_SUBTYPES = [
  'Birthday',
  'Anniversary',
  'Communion',
  'Christening',
  'Graduation',
  'Engagement',
  'Baby Shower',
  'Retirement',
  'Reunion',
];


const EVENT_TYPE_OPTIONS = [
  { value: 'wedding', label: 'Wedding' },
  { value: 'corporate', label: 'Corporate' },
  { value: 'private_party', label: 'Private Party' },
  { value: 'community', label: 'Community' },
  { value: 'charity', label: 'Charity' },
  { value: 'music_night', label: 'Music Night' },
  { value: 'restaurant_booking', label: 'Restaurant Booking' },
  { value: 'social', label: 'Social' },
  { value: 'other', label: 'Other' },
];

export default function Events({ selectedEvent: propSelectedEvent, onNavigate, onSelectEventInPage, navigateAction, clearNavigateAction }) {
  const { events, payments, venues, clients, addEvent, updateEvent, deleteEvent, sentEmails, addSentEmail, emailTemplates, addPayment, deletePayment, updateEventStage, addNote, addClient, eventTasks, tableBookings, addTableBooking, linkBookingToEvent, unlinkBookingFromEvent, deleteTableBooking } = useAppContext();
  const { notifySuccess, notifyError } = useNotify();
  const hasAutoExpanded = useRef(false);
  const expandedCardRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedStage, setSelectedStage] = useState('');
  const [selectedVenue, setSelectedVenue] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(navigateAction === 'addEvent');
  const [activeTab, setActiveTab] = useState('details');
  const [isAddingEvent, setIsAddingEvent] = useState(false);

  // Auto-open add form when navigated with action. Also supports
  // create-from-email which pre-fills client fields from an inbound email.
  useEffect(() => {
    if (navigateAction === 'addEvent') {
      setShowAddModal(true);
      clearNavigateAction?.();
    } else if (navigateAction && typeof navigateAction === 'object' && navigateAction.action === 'create-from-email') {
      setFormData({
        ...getEmptyFormData(),
        client: navigateAction.clientName || '',
        clientEmail: navigateAction.clientEmail || '',
        name: navigateAction.subject || '',
        notes: navigateAction.bodyText
          ? `From inbound email (${navigateAction.clientEmail || 'unknown'}):\n\n${navigateAction.bodyText}`
          : '',
      });
      setShowAddModal(true);
      clearNavigateAction?.();
    } else if (navigateAction && typeof navigateAction === 'object' && navigateAction.action === 'create-from-calendar') {
      // Pre-fill from the Dashboard's Annual Calendar — clicking a free date/venue.
      setFormData({
        ...getEmptyFormData(),
        date: navigateAction.date || '',
        venue_id: navigateAction.venue_id || '',
      });
      setShowAddModal(true);
      clearNavigateAction?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigateAction, clearNavigateAction]);
  const [formData, setFormData] = useState(getEmptyFormData());
  const [editData, setEditData] = useState(null);
  const [selectedFBPreset, setSelectedFBPreset] = useState('');
  const [customFBItem, setCustomFBItem] = useState('');
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [composerEvent, setComposerEvent] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    payment_date: todayKey(),
    paymentType: 'bank_transfer',
    category: 'fb_package',
    description: '',
  });
  const [newNote, setNewNote] = useState('');
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const fileInputRef = useRef(null);

  // Client autocomplete state
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);

  // Send-to-Restaurant modal state
  const [showSendToRestaurant, setShowSendToRestaurant] = useState(false);
  const [sendToRestaurantEvent, setSendToRestaurantEvent] = useState(null);
  const [sendToRestaurantData, setSendToRestaurantData] = useState({
    venue: 'reynards',
    sitting: 'evening',
    time: '19:00',
    covers: '',
  });
  const [sendToRestaurantBusy, setSendToRestaurantBusy] = useState(false);

  // Auto-expand event when navigated to from Pipeline or Dashboard
  useEffect(() => {
    if (propSelectedEvent && !hasAutoExpanded.current) {
      hasAutoExpanded.current = true;
      // Clear any filters so the event is visible in the list
      setSearchTerm('');
      setSelectedType('All');
      setSelectedStage('');
      setSelectedVenue('');
      handleCardClick(propSelectedEvent);
      // Scroll to the expanded card after a brief delay for render
      setTimeout(() => {
        if (expandedCardRef.current) {
          expandedCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 150);
    }
  }, [propSelectedEvent]);

  // Filter and search logic
  const filteredEvents = useMemo(() => {
    // Weddings are managed here too (Oct 2026: Wedding Pipeline page retired
    // from the menu). Use the Weddings filter to see only weddings.
    let result = events.slice();

    // Hide archived events by default — only show when explicitly filtered
    if (selectedStage !== 'archived') {
      result = result.filter((evt) => evt.stage !== 'archived');
    }

    // Search by name, client, type
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (evt) =>
          evt.name.toLowerCase().includes(term) ||
          evt.client.toLowerCase().includes(term) ||
          evt.type.toLowerCase().includes(term)
      );
    }

    // Filter by event type
    const typeValue = EVENT_TYPES[selectedType];
    if (typeValue) {
      result = result.filter((evt) => (evt.type || '').toLowerCase() === typeValue);
    }

    // Filter by stage
    if (selectedStage === '__needs_details') {
      result = result.filter((evt) => ['provisional', 'urgent'].includes(getEventDetailStatus(evt).status));
    } else if (selectedStage === '__urgent') {
      result = result.filter((evt) => getEventDetailStatus(evt).status === 'urgent');
    } else if (selectedStage) {
      result = result.filter((evt) => evt.stage === selectedStage);
    }

    // Filter by venue
    if (selectedVenue) {
      result = result.filter((evt) => evt.venue_id === selectedVenue);
    }

    // Sort
    if (sortBy === 'date') {
      result.sort((a, b) => new Date(a.date) - new Date(b.date));
    } else if (sortBy === 'name') {
      result.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === 'revenue') {
      result.sort((a, b) => (b.packageTotal || 0) - (a.packageTotal || 0));
    }

    return result;
  }, [events, searchTerm, selectedType, selectedStage, selectedVenue, sortBy]);

  // Calculate stats
  const stats = useMemo(() => {
    const confirmed = filteredEvents.filter((e) => e.stage === 'confirmed' || e.stage === 'deposit_paid' || e.stage === 'complete');
    const totalRevenue = confirmed.reduce((sum, evt) => sum + (evt.packageTotal || 0) + (evt.roomHire || 0), 0);
    const totalOutstanding = confirmed.reduce((sum, evt) => sum + calculateBalance(evt, payments), 0);

    return {
      totalEvents: filteredEvents.length,
      totalRevenue,
      totalOutstanding,
    };
  }, [filteredEvents, payments]);

  // Red box around required fields still empty, and a red count on each tab.
  const missF = missingFieldClass(formData);
  const missE = missingFieldClass(editData);
  const missTabsF = missingCountByTab(formData);
  const missTabsE = missingCountByTab(editData);
  const tabMissBadge = (n) => (n > 0
    ? <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-red-600 text-white leading-none" title={`${n} required field${n === 1 ? '' : 's'} missing`}>{n}</span>
    : null);

  function getEmptyFormData() {
    return {
      clientId: null,
      clientName: '',
      eventName: '',
      type: 'private_party',
      date: '',
      endDate: '',
      venue_id: '',
      guests: 0,
      stage: 'enquiry',
      notes: '',
      contactPhone: '',
      contactEmail: '',
      // Blank on purpose (Oct 2026): defaults made every booking look
      // complete. Reception must choose times and setup.
      startTime: '',
      endTime: '',
      setup: '',
      fbItems: [],
      equipment: [],
      subtype: '',
      roomHire: 0,
      pricePerHead: 0,
      packageTotal: 0,
      consumption: 0,
      billingType: 'group',
      depositPaid: 0,
      contractSigned: false,
      contractDate: '',
      dietaryRequirements: '',
      dietaryVeg: '',
      dietaryVegan: '',
      dietaryCoeliac: '',
      dietaryAllergens: '',
      dietaryNA: false,
      musicOnly: false,
      foodServiceTime: '',
      setupRequirements: '',
      menuNotes: '',
    };
  }

  function handleAddClick() {
    setFormData(getEmptyFormData());
    setIsAddingEvent(true);
    setExpandedEventId(null);
    setEditData(null);
    setActiveTab('details');
  }

  function handleCloneEvent(event) {
    const clonedData = {
      ...getEmptyFormData(),
      eventName: event.name + ' (Copy)',
      clientName: event.client,
      // Carry the same client link forward — a clone is normally "same
      // client, different date." If it turns out to be a different client,
      // picking a new name from the autocomplete overwrites this via
      // handleSelectClient before save.
      clientId: event.clientId || null,
      type: event.type,
      venue_id: event.venue_id,
      guests: event.guests,
      startTime: event.startTime,
      endTime: event.endTime,
      contactPhone: event.clientPhone,
      contactEmail: event.clientEmail,
      pricePerHead: event.pricePerHead,
      packageTotal: event.packageTotal,
      roomHire: event.roomHire,
      billingType: event.billingType,
      setup: event.setup,
      equipment: event.equipment || [],
      fbItems: event.fbItems || [],
      setupRequirements: event.setupRequirements || '',
      menuNotes: event.menuNotes || '',
      dietaryRequirements: event.dietaryRequirements || '',
      dietaryVeg: event.dietaryVeg ?? '',
      dietaryVegan: event.dietaryVegan ?? '',
      dietaryCoeliac: event.dietaryCoeliac ?? '',
      dietaryAllergens: event.dietaryAllergens ?? '',
        dietaryNA: !!event.dietaryNA,
        musicOnly: !!event.musicOnly,
      foodServiceTime: event.foodServiceTime ?? '',
      notes: event.notes,
      subtype: event.subtype || '',
      date: '',
      endDate: '',
      stage: 'enquiry',
    };
    setFormData(clonedData);
    setIsAddingEvent(true);
    setExpandedEventId(null);
    setEditData(null);
    setActiveTab('details');
  }

  function handleCardClick(event) {
    if (expandedEventId === event.id) {
      setExpandedEventId(null);
      setEditData(null);
      setActiveTab('details');
    } else {
      setExpandedEventId(event.id);
      setEditData({
        // Carry the existing client link forward. Without this, every save
        // of an already-linked event looked like "no client selected" and
        // silently inserted a duplicate client row on every routine edit.
        clientId: event.clientId || null,
        clientName: event.client || '',
        eventName: event.name || '',
        type: event.type || 'private_party',
        date: event.date || '',
        endDate: event.endDate || '',
        venue_id: event.venue_id || '',
        guests: event.guests || 0,
        stage: event.stage || 'enquiry',
        notes: event.notes || '',
        contactPhone: event.clientPhone || '',
        contactEmail: event.clientEmail || '',
        startTime: event.startTime || '',
        endTime: event.endTime || '',
        setup: event.setup || '',
        fbItems: event.fbItems || [],
        equipment: event.equipment || [],
        subtype: event.subtype || '',
        roomHire: event.roomHire || 0,
        pricePerHead: event.pricePerHead || 0,
        packageTotal: event.packageTotal || 0,
        consumption: event.consumption || 0,
        billingType: event.billingType || 'group',
        depositPaid: event.depositPaid || 0,
        contractSigned: event.contractSigned || false,
        contractDate: event.contractDate || '',
        brideName: event.brideName || '',
        groomName: event.groomName || '',
        dietaryRequirements: event.dietaryRequirements || '',
        dietaryVeg: event.dietaryVeg ?? '',
        dietaryVegan: event.dietaryVegan ?? '',
        dietaryCoeliac: event.dietaryCoeliac ?? '',
        dietaryAllergens: event.dietaryAllergens ?? '',
        dietaryNA: !!event.dietaryNA,
        musicOnly: !!event.musicOnly,
        foodServiceTime: event.foodServiceTime ?? '',
        setupRequirements: event.setupRequirements || '',
        menuNotes: event.menuNotes || '',
      });
      setSelectedFBPreset('');
      setCustomFBItem('');
      setActiveTab('details');
    }
  }

  function handleFormChange(e) {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? (parseFloat(value) || 0) : value),
    }));
  }

  function handleEditChange(e) {
    const { name, value, type, checked } = e.target;
    setEditData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? (parseFloat(value) || 0) : value),
    }));
  }

  function handleAddFBItem() {
    if (!editData) return;
    const newItems = [...editData.fbItems];
    if (selectedFBPreset && !newItems.includes(selectedFBPreset)) {
      newItems.push(selectedFBPreset);
    } else if (customFBItem && !newItems.includes(customFBItem)) {
      newItems.push(customFBItem);
    }
    setEditData({ ...editData, fbItems: newItems });
    setSelectedFBPreset('');
    setCustomFBItem('');
  }

  function handleRemoveFBItem(index) {
    if (!editData) return;
    setEditData({
      ...editData,
      fbItems: editData.fbItems.filter((_, i) => i !== index),
    });
  }

  function handleClientNameChange(e, isEdit = false) {
    const value = e.target.value;
    const setData = isEdit ? setEditData : setFormData;
    const currentData = isEdit ? editData : formData;

    setData({ ...currentData, clientName: value });

    if (value.length >= 2 && clients) {
      const matches = clients.filter((c) =>
        c.name.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5);
      setClientSuggestions(matches);
      setShowClientSuggestions(matches.length > 0);
    } else {
      setShowClientSuggestions(false);
    }
  }

  function handleSelectClient(client, isEdit = false) {
    const setData = isEdit ? setEditData : setFormData;
    const currentData = isEdit ? editData : formData;

    setData({
      ...currentData,
      clientName: client.name,
      contactPhone: client.phone || currentData.contactPhone,
      contactEmail: client.email || currentData.contactEmail,
      clientId: client.id,
    });
    setShowClientSuggestions(false);
  }

  // The date picker's `min` attribute on the End Date field is a soft hint
  // only — typing a date directly bypasses it — and there was no check at
  // all on guest count. A backwards date range breaks anything that spans
  // it, and a negative guest count silently produces a nonsensical package
  // total (price × guests) downstream. Shared by both the new-event and
  // edit-event save paths so the two forms can't drift apart.
  function validateEventDatesAndGuests(data) {
    if (data.endDate && data.date && data.endDate < data.date) {
      return 'End date cannot be before the start date';
    }
    const guests = Number(data.guests);
    if (data.guests !== '' && data.guests != null && Number.isFinite(guests) && guests < 0) {
      return 'Guest count cannot be negative';
    }
    return null;
  }

  async function handleSaveEvent() {
    if (!formData.eventName || !formData.clientName || !formData.date || !formData.venue_id) {
      notifyError('Please fill in all required fields');
      return;
    }
    const dateGuestsError = validateEventDatesAndGuests(formData);
    if (dateGuestsError) {
      notifyError(dateGuestsError);
      return;
    }
    // Bookings within 7 days must be complete before they can be saved.
    const detailBlock = getDetailSaveBlock(formData, null);
    if (detailBlock) {
      notifyError(detailBlock);
      return;
    }

    // Block saving onto a venue that's closed for the date (hotel shut for
    // Christmas, the January off-season, etc.) — same guardrail the
    // restaurant table-booking side already has. Fetched fresh right before
    // saving rather than cached, so a closure added moments ago is caught.
    try {
      const closureDates = [formData.date, formData.endDate].filter(Boolean);
      const closureBlocks = await fetchVenueBlocksByDates(closureDates);
      const closureReason = getVenueClosureReason(formData.venue_id, formData.date, formData.endDate, closureBlocks);
      if (closureReason) {
        notifyError(`This venue is closed for the selected date(s): ${closureReason}. Choose a different date or venue.`);
        return;
      }
    } catch (err) {
      // Non-fatal: don't block a legitimate booking over a network hiccup
      // checking closures — just log it and let the save proceed.
      console.error('[Events] Could not verify venue closures:', err);
    }

    try {
      await addEvent({
        name: formData.eventName,
        client: formData.clientName,
        clientId: formData.clientId || null,
        type: formData.type,
        date: formData.date,
        endDate: formData.endDate || null,
        venue_id: formData.venue_id,
        guests: formData.guests,
        stage: formData.stage,
        notes: formData.notes,
        clientPhone: formData.contactPhone,
        clientEmail: formData.contactEmail,
        startTime: formData.startTime,
        endTime: formData.endTime,
        setup: formData.setup,
        fbItems: formData.fbItems,
        equipment: formData.equipment,
        subtype: formData.subtype,
        roomHire: formData.roomHire,
        pricePerHead: formData.pricePerHead,
        packageTotal: formData.packageTotal,
        consumption: formData.consumption,
        billingType: formData.billingType,
        depositPaid: formData.depositPaid,
        contractSigned: formData.contractSigned,
        contractDate: formData.contractDate,
        dietaryRequirements: formData.dietaryRequirements,
        dietaryVeg: formData.dietaryVeg ?? '',
        dietaryVegan: formData.dietaryVegan ?? '',
        dietaryCoeliac: formData.dietaryCoeliac ?? '',
        dietaryAllergens: formData.dietaryAllergens ?? '',
        dietaryNA: !!formData.dietaryNA,
        musicOnly: !!formData.musicOnly,
        foodServiceTime: formData.foodServiceTime ?? '',
        setupRequirements: formData.setupRequirements,
        menuNotes: formData.menuNotes,
      });

    } catch (err) {
      notifyError('Failed to save enquiry. Please try again.');
      console.error('[Events] Save failed:', err);
      return;
    }

    // Auto-create client if not linked to an existing one. Defensive
    // name-match guard: even if clientId wasn't set (e.g. the guest name
    // was typed rather than picked from the autocomplete), don't create a
    // second client row for a name that already exists. This runs AFTER
    // the event save is confirmed and in its own try/catch — a failure
    // here must not claim the whole save failed (the event already saved
    // fine) or invite a retry that would create a duplicate event.
    const matchedExistingClient = !formData.clientId && formData.clientName
      ? clients?.find((c) => c.name.trim().toLowerCase() === formData.clientName.trim().toLowerCase())
      : null;
    if (!formData.clientId && formData.clientName && !matchedExistingClient) {
      const typeToTag = {
        wedding: 'wedding',
        corporate: 'corporate',
        community: 'community',
        charity: 'charity',
        private_party: 'private',
        music_night: 'other',
        restaurant_booking: 'other',
        social: 'other',
        other: 'other',
      };
      try {
        await addClient({
          name: formData.clientName,
          phone: formData.contactPhone || '',
          email: formData.contactEmail || '',
          tag: typeToTag[formData.type] || 'other',
        });
      } catch (err) {
        console.error('[Events] Auto-create client failed:', err);
        notifyError(`Event saved, but failed to save a client record for "${formData.clientName}": ${err?.message || 'unknown error'}`);
      }
    }

    setIsAddingEvent(false);
    setShowAddModal(false);
    setFormData(getEmptyFormData());
  }

  async function handleSaveEditedEvent() {
    if (!editData || !editData.eventName || !editData.clientName || !editData.date || !editData.venue_id) {
      notifyError('Please fill in all required fields');
      return;
    }
    const dateGuestsError = validateEventDatesAndGuests(editData);
    if (dateGuestsError) {
      notifyError(dateGuestsError);
      return;
    }
    // Moving an incomplete event into the 7-day window is treated like a new
    // late booking. Events already inside the window may save partially.
    const detailBlock = getDetailSaveBlock(editData, events.find((e) => e.id === expandedEventId) || null);
    if (detailBlock) {
      notifyError(detailBlock);
      return;
    }

    // Same closure guardrail as handleSaveEvent, but only when the venue or
    // date actually changed — an edit that leaves placement untouched (e.g.
    // updating guest count on an event booked before the closure existed)
    // must not suddenly get blocked by a closure added afterwards.
    const originalEventForClosureCheck = events.find((e) => e.id === expandedEventId);
    const placementChanged = !originalEventForClosureCheck
      || originalEventForClosureCheck.venue_id !== editData.venue_id
      || originalEventForClosureCheck.date !== editData.date
      || (originalEventForClosureCheck.endDate || '') !== (editData.endDate || '');
    if (placementChanged) {
      try {
        const closureDates = [editData.date, editData.endDate].filter(Boolean);
        const closureBlocks = await fetchVenueBlocksByDates(closureDates);
        const closureReason = getVenueClosureReason(editData.venue_id, editData.date, editData.endDate, closureBlocks);
        if (closureReason) {
          notifyError(`This venue is closed for the selected date(s): ${closureReason}. Choose a different date or venue.`);
          return;
        }
      } catch (err) {
        console.error('[Events] Could not verify venue closures:', err);
      }
    }

    try {
      await updateEvent(expandedEventId, {
        name: editData.eventName,
        client: editData.clientName,
        clientId: editData.clientId || null,
        type: editData.type,
        date: editData.date,
        endDate: editData.endDate || null,
        venue_id: editData.venue_id,
        guests: editData.guests,
        stage: editData.stage,
        notes: editData.notes,
        clientPhone: editData.contactPhone,
        clientEmail: editData.contactEmail,
        startTime: editData.startTime,
        endTime: editData.endTime,
        setup: editData.setup,
        fbItems: editData.fbItems,
        equipment: editData.equipment,
        subtype: editData.subtype,
        roomHire: editData.roomHire,
        pricePerHead: editData.pricePerHead,
        packageTotal: editData.packageTotal,
        consumption: editData.consumption,
        billingType: editData.billingType,
        depositPaid: editData.depositPaid,
        contractSigned: editData.contractSigned,
        contractDate: editData.contractDate,
        brideName: editData.brideName ?? '',
        groomName: editData.groomName ?? '',
        dietaryRequirements: editData.dietaryRequirements,
        dietaryVeg: editData.dietaryVeg ?? '',
        dietaryVegan: editData.dietaryVegan ?? '',
        dietaryCoeliac: editData.dietaryCoeliac ?? '',
        dietaryAllergens: editData.dietaryAllergens ?? '',
        dietaryNA: !!editData.dietaryNA,
        musicOnly: !!editData.musicOnly,
        foodServiceTime: editData.foodServiceTime ?? '',
        setupRequirements: editData.setupRequirements,
        menuNotes: editData.menuNotes,
      });
    } catch (err) {
      notifyError('Failed to save changes. Please try again.');
      console.error('[Events] Update failed:', err);
      return;
    }

    // Auto-create client if not linked to an existing one. Same name-match
    // guard as handleSaveEvent — see comment there. Runs after the event
    // save is confirmed, in its own try/catch — a failure here must not
    // claim the whole save failed (the event edit already saved fine).
    const matchedExistingClient = !editData.clientId && editData.clientName
      ? clients?.find((c) => c.name.trim().toLowerCase() === editData.clientName.trim().toLowerCase())
      : null;
    if (!editData.clientId && editData.clientName && !matchedExistingClient) {
      const typeToTag = {
        wedding: 'wedding',
        corporate: 'corporate',
        community: 'community',
        charity: 'charity',
        private_party: 'private',
        music_night: 'other',
        restaurant_booking: 'other',
        social: 'other',
        other: 'other',
      };
      try {
        await addClient({
          name: editData.clientName,
          phone: editData.contactPhone || '',
          email: editData.contactEmail || '',
          tag: typeToTag[editData.type] || 'other',
        });
      } catch (err) {
        console.error('[Events] Auto-create client failed:', err);
        notifyError(`Changes saved, but failed to save a client record for "${editData.clientName}": ${err?.message || 'unknown error'}`);
      }
    }

    setExpandedEventId(null);
    setEditData(null);
    setActiveTab('details');
  }

  async function handleDeleteEvent() {
    if (!window.confirm('Are you sure you want to delete this event? This cannot be undone.')) return;
    const targetId = expandedEventId;
    setExpandedEventId(null);
    setEditData(null);
    try {
      await deleteEvent(targetId);
    } catch (err) {
      // 23503 = Postgres foreign_key_violation. The most common cause is
      // payments_event_id_fkey (migration 014 made it ON DELETE RESTRICT
      // on purpose, so a deleted event can never silently take its payment
      // history with it) — tell staff why in plain English and point them
      // at Cancelled/Archived instead of showing the raw DB error text.
      if (err?.code === '23503') {
        const paid = (payments || [])
          .filter((p) => p.event_id === targetId)
          .reduce((sum, p) => sum + (p.amount || 0), 0);
        notifyError(
          paid > 0
            ? `This event has ${fmt(paid)} in payments recorded, so it can't be deleted — mark it Cancelled or Archived instead, or remove the payments first.`
            : "This event can't be deleted because other records still reference it — mark it Cancelled or Archived instead."
        );
      } else {
        notifyError('Failed to delete event: ' + (err?.message || 'unknown error') + ' — it has been restored.');
      }
    }
  }

  function handleCollapseCard() {
    setExpandedEventId(null);
    setEditData(null);
  }

  // ─── Send event to a restaurant table ───

  /** Get the table booking linked to a given event (if any) */
  function getLinkedTableBookingForEvent(eventId) {
    return (tableBookings || []).find((b) => b.linkedEventId === eventId && b.source === 'event');
  }

  /** Whether an event is eligible to be sent to a restaurant (excludes weddings & big functions) */
  function canSendEventToRestaurant(event) {
    if (!event) return false;
    if (event.type?.toLowerCase() === 'wedding') return false;
    const guests = parseInt(event.guests) || 0;
    if (guests > 50) return false;
    if (getLinkedTableBookingForEvent(event.id)) return false;
    return true;
  }

  function handleOpenSendToRestaurant(event) {
    const guessedSitting = guessSittingFromTime(event.startTime) || RESTAURANT_SITTINGS[2];
    const guests = event.guests || 2;
    // Reynards requires 6+ every day, so default to a venue that's actually
    // valid for the party size. Saves the staff a dropdown change.
    const defaultVenue = guests >= 6 ? 'reynards' : 'randr';
    setSendToRestaurantEvent(event);
    setSendToRestaurantData({
      venue: defaultVenue,
      sitting: guessedSitting.id,
      time: event.startTime || `${String(guessedSitting.startH).padStart(2, '0')}:00`,
      covers: String(guests),
    });
    setShowSendToRestaurant(true);
  }

  function handleCloseSendToRestaurant() {
    setShowSendToRestaurant(false);
    setSendToRestaurantEvent(null);
    setSendToRestaurantBusy(false);
  }

  async function handleConfirmSendToRestaurant() {
    const event = sendToRestaurantEvent;
    if (!event) return;
    const { venue, sitting, time, covers } = sendToRestaurantData;
    const coversNum = parseInt(covers);

    if (!venue) { notifyError('Please pick a restaurant.'); return; }
    if (!sitting) { notifyError('Please pick a sitting.'); return; }
    if (!time) { notifyError('Please set a time.'); return; }
    if (!coversNum || coversNum < 1) { notifyError('Covers must be at least 1.'); return; }

    const sittingDef = RESTAURANT_SITTINGS.find((s) => s.id === sitting);
    const validSittings = getSittingsForVenueAndDate(venue, event.date);
    if (!validSittings.some((s) => s.id === sitting)) {
      const vName = RESTAURANT_VENUES.find((v) => v.id === venue)?.name || venue;
      notifyError(`${vName} is not open for ${sitting} on ${event.date}.`);
      return;
    }
    if (sittingDef) {
      const [h, m] = time.split(':').map(Number);
      const timeMins = h * 60 + (m || 0);
      if (timeMins < sittingDef.startH * 60 || timeMins >= sittingDef.endH * 60) {
        notifyError(`Time ${time} is outside the ${sittingDef.label} sitting (${sittingDef.time}).`);
        return;
      }
    }
    const venueCap = RESTAURANT_VENUES.find((v) => v.id === venue)?.capacity || 60;
    if (coversNum > venueCap) {
      notifyError(`${RESTAURANT_VENUES.find((v) => v.id === venue)?.name} seats a max of ${venueCap}.`);
      return;
    }
    const minCovers = getMinCoversForRestaurant(venue);
    if (coversNum < minCovers) {
      notifyError(`Reynards requires a minimum of ${minCovers} covers.`);
      return;
    }

    setSendToRestaurantBusy(true);
    try {
      const bookingData = {
        id: `booking-${Date.now()}`,
        guestName: event.client || event.name,
        phone: event.clientPhone || '',
        email: event.clientEmail || '',
        venue,
        date: event.date,
        time,
        covers: coversNum,
        source: 'event',
        roomNumber: '',
        status: 'booked',
        tableNumber: '',
        notes: `From event: ${event.name}${event.notes ? `\n${event.notes}` : ''}`,
        // Link back to the source event so we can unwind this later.
        linkedEventId: event.id,
        // Stash the event's original venue so we can restore it if the booking is unlinked.
        eventPreviousVenueId: event.venue_id || null,
      };
      const newBooking = await addTableBooking(bookingData);
      if (newBooking?._dbError) {
        notifyError(`Saved locally but database save failed: ${newBooking._dbError}. This booking will be lost on refresh.`);
        setSendToRestaurantBusy(false);
        return;
      }
      // Release the event from Room Diary — it now lives as a restaurant booking.
      await updateEvent(event.id, { venue_id: null });
      const vName = RESTAURANT_VENUES.find((v) => v.id === venue)?.name || venue;
      notifySuccess(`Sent to ${vName} (${sittingDef?.label || sitting} sitting, ${coversNum} covers)`);
      handleCloseSendToRestaurant();
    } catch (err) {
      console.error('[Events] Send to restaurant failed:', err);
      notifyError('Failed to send to restaurant: ' + err.message);
      setSendToRestaurantBusy(false);
    }
  }

  async function handleUnlinkFromRestaurant(event) {
    const booking = getLinkedTableBookingForEvent(event.id);
    if (!booking) return;
    const previousVenueId = booking.eventPreviousVenueId || null;
    const restoreVenue = (venues || []).find((v) => v.id === previousVenueId);
    const restoreMsg = restoreVenue
      ? `\n\nThe event will return to "${restoreVenue.name}" in Room Diary.`
      : `\n\nThe event will return to the Events list (no venue assigned).`;
    if (!window.confirm(`Remove the linked table booking at ${RESTAURANT_VENUES.find((v) => v.id === booking.venue)?.name || booking.venue}?${restoreMsg}`)) return;
    try {
      await deleteTableBooking(booking.id);
      // Restore the event's original venue so it shows back up in Room Diary.
      if (previousVenueId) {
        await updateEvent(event.id, { venue_id: previousVenueId });
      }
    } catch (err) {
      console.error('[Events] Unlink from restaurant failed:', err);
      notifyError('Failed to remove table booking: ' + err.message);
    }
  }

  async function handleDeletePayment(payment) {
    if (!payment || !payment.id) return;
    // Skip DB for temp / offline-only payments (these only exist in state).
    const isTemp = typeof payment.id === 'string' && payment.id.startsWith('temp_');
    const label = payment.description
      ? `"${payment.description}" (${fmt(payment.amount)})`
      : `${fmt(payment.amount)} on ${fmtDate(payment.payment_date)}`;
    if (!window.confirm(`Delete payment ${label}?\n\nThis cannot be undone.`)) return;
    try {
      await deletePayment(payment.id);
      if (!isTemp) notifySuccess('Payment deleted');
    } catch (err) {
      notifyError('Failed to delete payment: ' + (err?.message || 'unknown error'));
    }
  }

  async function handleRecordPayment(eventId) {
    const amount = parseFloat(paymentFormData.amount);
    if (!amount || amount <= 0) return;
    try {
      const saved = await addPayment({
        eventId: eventId,
        amount,
        payment_date: paymentFormData.payment_date,
        paymentType: paymentFormData.paymentType,
        category: paymentFormData.category,
        type: 'Payment',
        description: paymentFormData.description,
      });
      // Warn if the save silently fell back to a temporary in-memory payment.
      if (saved && typeof saved.id === 'string' && saved.id.startsWith('temp_')) {
        notifyError('Payment was not saved to the database. Check your connection and try again.');
        return;
      }
      notifySuccess('Payment recorded');
    } catch (err) {
      notifyError('Failed to save payment: ' + (err?.message || 'unknown error'));
      return;
    }
    setPaymentFormData({
      amount: '',
      payment_date: todayKey(),
      paymentType: 'bank_transfer',
      category: 'fb_package',
      description: '',
    });
    setShowPaymentForm(false);
  }

  async function handleAddNote(eventId) {
    if (!newNote.trim()) return;
    const noteText = newNote.trim();
    setNewNote('');
    try {
      await addNote?.(eventId, noteText);
    } catch (err) {
      notifyError('Failed to save note: ' + (err?.message || 'unknown error') + ' — please try again.');
      setNewNote(noteText);
    }
  }

  function handleMoveToStage(eventId, targetStage) {
    updateEventStage(eventId, targetStage);
  }

  async function handleFileUpload(eventId, files) {
    if (!files || files.length === 0) return;
    setUploadingAttachment(true);
    try {
      const event = events.find(e => e.id === eventId);
      const existingAttachments = event?.attachments || [];
      const newAttachments = [];

      for (const file of files) {
        const meta = await uploadAttachment(eventId, file);
        newAttachments.push(meta);
      }

      const allAttachments = [...existingAttachments, ...newAttachments];
      updateEvent(eventId, { attachments: allAttachments });
      if (editData) {
        setEditData(prev => ({ ...prev, attachments: allAttachments }));
      }
    } catch (err) {
      console.error('Upload failed:', err);
      notifyError('Failed to upload file. Please try again.');
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  async function handleDeleteAttachment(eventId, attachment) {
    if (!window.confirm(`Delete "${attachment.name}"?`)) return;
    try {
      await deleteAttachment(attachment.path);
      const event = events.find(e => e.id === eventId);
      const updated = (event?.attachments || []).filter(a => a.id !== attachment.id);
      updateEvent(eventId, { attachments: updated });
      if (editData) {
        setEditData(prev => ({ ...prev, attachments: updated }));
      }
    } catch (err) {
      console.error('Delete failed:', err);
      notifyError('Failed to delete file.');
    }
  }

  async function handleDownloadAttachment(attachment) {
    try {
      const url = await getAttachmentUrl(attachment.path);
      window.open(url, '_blank');
    } catch (err) {
      // Fallback to public URL
      window.open(attachment.url, '_blank');
    }
  }

  function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  function closeAddModal() {
    setShowAddModal(false);
    setFormData(getEmptyFormData());
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900">Events</h1>
          <button
            onClick={handleAddClick}
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-medium transition min-h-[44px] sm:min-h-auto"
          >
            <Plus size={20} />
            <span className="hidden sm:inline">Add Event</span>
            <span className="sm:hidden">Add</span>
          </button>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 md:mb-8">
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-slate-200">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-slate-600 text-xs md:text-sm font-medium">Total Events</p>
                <p className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">{stats.totalEvents}</p>
              </div>
              <Calendar className="text-slate-300" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-slate-200">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-slate-600 text-xs md:text-sm font-medium">Total Revenue</p>
                <p className="text-2xl md:text-3xl font-bold text-emerald-600 mt-1">{fmt(stats.totalRevenue)}</p>
              </div>
              <TrendingUp className="text-emerald-300" size={32} />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm border border-slate-200">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-slate-600 text-xs md:text-sm font-medium">Outstanding</p>
                <p className="text-2xl md:text-3xl font-bold text-amber-600 mt-1">{fmt(stats.totalOutstanding)}</p>
              </div>
              <Users className="text-amber-300" size={32} />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200 mb-6">
          <div className="flex flex-col lg:flex-row gap-3 lg:gap-4 items-start lg:items-center">
            <div className="w-full lg:flex-1 relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            <div className="flex flex-wrap gap-2 w-full lg:w-auto">
              {Object.keys(EVENT_TYPES).map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className={`flex-1 sm:flex-none px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition min-h-[44px] sm:min-h-auto flex items-center justify-center ${
                    selectedType === type
                      ? 'bg-emerald-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
            <select
              value={selectedStage}
              onChange={(e) => setSelectedStage(e.target.value)}
              className="w-full lg:w-auto px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[44px] lg:min-h-auto"
            >
              <option value="">All Stages</option>
              <option value="__needs_details">⚠ Needs details (orange + red)</option>
              <option value="__urgent">⚠ Under a week, details missing (red)</option>
              {WEDDING_PIPELINE_STAGES.map((s) => (
                <option key={s} value={s}>{STAGE_LABELS[s] || s}</option>
              ))}
              <option value="cancelled">Cancelled</option>
              <option value="archived">Archived</option>
            </select>
            <select
              value={selectedVenue}
              onChange={(e) => setSelectedVenue(e.target.value)}
              className="w-full lg:w-auto px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[44px] lg:min-h-auto"
            >
              <option value="">All Venues</option>
              {venues.map((v) => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full lg:w-auto px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 min-h-[44px] lg:min-h-auto"
            >
              <option value="date">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="revenue">Sort by Revenue</option>
            </select>
          </div>
        </div>

        {/* Event Cards */}
        <div className="space-y-4">
          {/* Add Event Card - Inline at top */}
          {(showAddModal || isAddingEvent) && (
            <div className="bg-white rounded-lg shadow-sm border border-emerald-300 ring-1 ring-emerald-200">
              <div className="p-4 md:p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Add New Event</h3>

                {/* Tab Bar */}
                <div className="flex border-b border-slate-200 mb-6">
                  {['details', 'setup', 'financials', 'tasks'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
                        activeTab === tab
                          ? 'border-emerald-600 text-emerald-600'
                          : 'border-transparent text-slate-600 hover:text-slate-900'
                      }`}
                    >
                      {tab === 'details' && 'Details'}
                      {tab === 'setup' && 'Event Setup'}
                      {tab === 'financials' && 'Financials'}
                      {tab === 'tasks' && 'Tasks'}
                      {tabMissBadge(missTabsF[tab])}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                {activeTab === 'details' && <MusicOnlyToggle data={formData} onChange={handleFormChange} className="mb-4" />}
                {activeTab === 'details' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Event Name *</label>
                        <input name="eventName" value={formData.eventName} onChange={handleFormChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missF('name')}`} />
                      </div>
                      <div className="relative">
                        <label className="block text-xs font-medium text-slate-500 mb-1">Client Name *</label>
                        <input
                          name="clientName"
                          value={formData.clientName}
                          onChange={(e) => handleClientNameChange(e, false)}
                          onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
                          className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missF('client')}`}
                          autoComplete="off"
                        />
                        {showClientSuggestions && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {clientSuggestions.map((client) => (
                              <button
                                key={client.id}
                                type="button"
                                onClick={() => handleSelectClient(client, false)}
                                className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm"
                              >
                                <span className="font-medium">{client.name}</span>
                                {client.company && <span className="text-slate-400 ml-2">{client.company}</span>}
                                {client.tag && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-xs">{client.tag}</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Start Date *</label>
                        <input type="date" name="date" value={formData.date} onChange={handleFormChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missF('date')}`} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">End Date <span className="text-slate-400 font-normal">(multi-day)</span></label>
                        <input type="date" name="endDate" value={formData.endDate} onChange={handleFormChange} min={formData.date || undefined} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Type</label>
                        <select name="type" value={formData.type} onChange={handleFormChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missF('type')}`}>
                          {EVENT_TYPE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Subtype</label>
                        <select name="subtype" value={formData.subtype} onChange={handleFormChange} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                          <option value="">Select Subtype</option>
                          {EVENT_SUBTYPES.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Venue *</label>
                        <select name="venue_id" value={formData.venue_id} onChange={handleFormChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missF('venue')}`}>
                          <option value="">Select Venue</option>
                          {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Guests</label>
                        <input type="number" name="guests" min="0" value={formData.guests} onChange={handleFormChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missF('guests')}`} />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">Start Time</label>
                          <TimeSelect name="startTime" value={formData.startTime} onChange={handleFormChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missF('startTime')}`} />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-500 mb-1">End Time</label>
                          <TimeSelect name="endTime" value={formData.endTime} onChange={handleFormChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missF('endTime')}`} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Phone</label>
                        <input name="contactPhone" value={formData.contactPhone} onChange={handleFormChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missF('contact')}`} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
                        <input name="contactEmail" value={formData.contactEmail} onChange={handleFormChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missF('contact')}`} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Notes</label>
                        <textarea name="notes" value={formData.notes} onChange={handleFormChange} rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'setup' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Setup Style *</label>
                        <select name="setup" value={formData.setup} onChange={handleFormChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missF('setup')}`}>
                          <option value="">Select setup</option>
                          {SETUP_STYLES.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Setup Requirements *</label>
                        <textarea name="setupRequirements" placeholder="Table setup: no. of tables, seats per table, top table, layout, linen &amp; extras (e.g. 8 rounds of 10, top table for 6, white linen, cake table)" value={formData.setupRequirements} onChange={handleFormChange} rows={3} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none${missF('setupRequirements')}`} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Menu Notes</label>
                        <textarea name="menuNotes" value={formData.menuNotes} onChange={handleFormChange} rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-2">Equipment</label>
                        <div className="flex flex-wrap gap-2">
                          {EQUIPMENT_OPTIONS.map((eq) => (
                            <button
                              key={eq}
                              onClick={() => {
                                const updated = formData.equipment.includes(eq)
                                  ? formData.equipment.filter((e) => e !== eq)
                                  : [...formData.equipment, eq];
                                setFormData({ ...formData, equipment: updated });
                              }}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                                formData.equipment.includes(eq)
                                  ? 'bg-emerald-600 text-white'
                                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                              }`}
                            >
                              {eq.replace(/_/g, ' ')}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-2">F&B Items</label>
                        <div className="flex flex-wrap gap-2">
                          {FB_PRESETS.map((preset) => (
                            <button
                              key={preset.value}
                              onClick={() => {
                                const updated = formData.fbItems.includes(preset.value)
                                  ? formData.fbItems.filter((f) => f !== preset.value)
                                  : [...formData.fbItems, preset.value];
                                setFormData({ ...formData, fbItems: updated });
                              }}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                                formData.fbItems.includes(preset.value)
                                  ? 'bg-emerald-600 text-white'
                                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                              }`}
                            >
                              {preset.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Dietary Requirements</label>
                        <textarea name="dietaryRequirements" value={formData.dietaryRequirements} onChange={handleFormChange} rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
                        <KitchenFields data={formData} onChange={handleFormChange} />
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'financials' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Price Per Head (€)</label>
                        <input type="number" name="pricePerHead" value={formData.pricePerHead || ''} onChange={(e) => {
                          const pph = parseFloat(e.target.value) || 0;
                          const autoTotal = pph * (formData.guests || 0);
                          setFormData({ ...formData, pricePerHead: pph, packageTotal: autoTotal });
                        }} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missF('pricing')}`} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Package Total (€)</label>
                        <input type="number" name="packageTotal" value={formData.packageTotal} onChange={handleFormChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missF('pricing')}`} />
                        {formData.pricePerHead > 0 && formData.guests > 0 && (
                          <p className="text-xs text-slate-400 mt-1">{fmt(formData.pricePerHead)} × {formData.guests} guests</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Room Hire (€)</label>
                        <input type="number" name="roomHire" value={formData.roomHire || 0} onChange={handleFormChange} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Billing Type</label>
                        <select name="billingType" value={formData.billingType} onChange={handleFormChange} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                          <option value="group">Group</option>
                          <option value="individual">Individual</option>
                          <option value="per_head">Per Head</option>
                        </select>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-500 mb-1">Stage</label>
                        <select name="stage" value={formData.stage} onChange={handleFormChange} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                          {getPipelineStages(formData.type).map((s) => <option key={s} value={s}>{STAGE_LABELS[s] || s}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'tasks' && (
                  <div className="mb-6">
                    <p className="text-sm text-slate-400 italic">Save the event first, then you can add tasks.</p>
                  </div>
                )}

                <div className="mb-3">
                  <DetailStatusPanel event={formData} isNew onGoToTab={setActiveTab} />
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-4 border-t border-slate-200">
                  <button onClick={() => { setIsAddingEvent(false); setShowAddModal(false); setFormData(getEmptyFormData()); }} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium transition">Cancel</button>
                  <button onClick={handleSaveEvent} className="flex items-center justify-center gap-2 px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition">
                    <Plus size={16} />
                    Create Event
                  </button>
                </div>
              </div>
            </div>
          )}

          {filteredEvents.length === 0 ? (
            <div className="bg-white rounded-lg p-8 md:p-12 shadow-sm border border-slate-200 text-center">
              <Calendar className="mx-auto text-slate-300 mb-4" size={40} />
              <p className="text-slate-500 text-base md:text-lg">No events found</p>
              <p className="text-slate-400 text-sm mt-1">Try adjusting your filters or add a new event</p>
            </div>
          ) : (
            filteredEvents.map((event) => {
              const venue = getVenue(event.venue_id, venues);
              const isExpanded = expandedEventId === event.id;
              const stageBadge = STAGE_COLOURS[event.stage] || 'bg-slate-100 text-slate-700';
              const detailStatus = getEventDetailStatus(event).status;
              const detailBorder = detailStatus === 'urgent' ? 'border-l-4 border-l-red-600' : detailStatus === 'provisional' ? 'border-l-4 border-l-orange-500' : detailStatus === 'booked' ? 'border-l-4 border-l-emerald-500' : detailStatus === 'music' ? 'border-l-4 border-l-violet-500' : '';

              return (
                <div
                  key={event.id}
                  ref={isExpanded ? expandedCardRef : null}
                  className={`bg-white rounded-lg shadow-sm border transition cursor-pointer ${detailBorder} ${
                    isExpanded ? 'border-emerald-300 ring-1 ring-emerald-200' : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => handleCardClick(event)}
                >
                  {/* Collapsed Card Header */}
                  <div className="p-4 md:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-slate-50 transition-colors duration-150">
                    <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                          <h3 className="font-semibold text-slate-900 truncate text-sm md:text-base">{event.name}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${stageBadge}`}>
                            {STAGE_LABELS[event.stage] || event.stage}
                          </span>
                          <DetailStatusBadge event={event} />
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mt-2 sm:mt-1 text-xs sm:text-sm text-slate-500">
                          <span className="truncate">{event.client}</span>
                          <span className="flex items-center gap-1 whitespace-nowrap"><Calendar size={12} /> {fmtDateRange(event.date, event.endDate)}</span>
                          {venue && <span className="flex items-center gap-1 whitespace-nowrap"><MapPin size={12} /> {venue.name}</span>}
                          <span className="flex items-center gap-1 whitespace-nowrap"><Users size={12} /> {event.guests}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 sm:gap-4 ml-0 sm:ml-4">
                      {(() => {
                        const fb = (event.packageTotal || 0) + (event.consumption || 0);
                        const rh = event.roomHire || 0;
                        const total = fb + rh;
                        const evtPayments = (payments || []).filter((p) => p.event_id === event.id);
                        const paid = evtPayments.reduce((s, p) => s + (p.amount || 0), 0);
                        return (
                          <div className="text-right whitespace-nowrap">
                            <div className="flex items-center gap-3 text-xs sm:text-sm">
                              {fb > 0 && (
                                <span className="text-slate-600">
                                  <span className="text-slate-400 text-xs">F&B</span> <span className="font-semibold text-slate-800">{fmt(fb)}</span>
                                </span>
                              )}
                              {rh > 0 && (
                                <span className="text-slate-600">
                                  <span className="text-slate-400 text-xs">Room</span> <span className="font-semibold text-slate-800">{fmt(rh)}</span>
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5 text-xs justify-end">
                              {total > 0 && <span className="text-slate-400">Total {fmt(total)}</span>}
                              <span className={`font-semibold ${paid >= total && total > 0 ? 'text-emerald-600' : paid > 0 ? 'text-amber-600' : 'text-red-500'}`}>
                                Paid {fmt(paid)}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                      <div
                        className="p-1 rounded hover:bg-slate-100 transition-all duration-200 flex items-center justify-center flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardClick(event);
                        }}
                      >
                        {isExpanded ? <ChevronUp size={20} className="text-slate-600 font-semibold" /> : <ChevronDown size={20} className="text-slate-600 font-semibold" />}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Detail with Tabbed Interface */}
                  {isExpanded && editData && (
                    <div className="border-t border-slate-200 p-4 md:p-6" onClick={(e) => e.stopPropagation()}>
                      {/* Linked Table Booking Banner */}
                      {(() => {
                        const linkedBooking = getLinkedTableBookingForEvent(event.id);
                        if (!linkedBooking) return null;
                        const vName = RESTAURANT_VENUES.find((v) => v.id === linkedBooking.venue)?.name || linkedBooking.venue;
                        const sitDef = RESTAURANT_SITTINGS.find((s) => {
                          const [h] = (linkedBooking.time || '0:00').split(':').map(Number);
                          return h >= s.startH && h < s.endH;
                        });
                        return (
                          <div className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-3 flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-sm text-purple-900">
                              <Utensils size={16} className="text-purple-600" />
                              <span>
                                Linked to table booking at <strong>{vName}</strong>
                                {sitDef && <> — {sitDef.label}</>} · {linkedBooking.time} · {linkedBooking.covers} covers
                              </span>
                            </div>
                            <button
                              onClick={() => handleUnlinkFromRestaurant(event)}
                              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 hover:bg-purple-100 rounded border border-purple-300 transition"
                              title="Remove the linked table booking"
                            >
                              <Unlink size={14} />
                              Remove
                            </button>
                          </div>
                        );
                      })()}
                      {/* Tab Bar */}
                      <div className="flex border-b border-slate-200 mb-6">
                        {['details', 'setup', 'financials', 'tasks', 'attachments'].map((tab) => {
                          const openTaskCount = tab === 'tasks' ? eventTasks.filter((t) => t.eventId === event.id && !t.completed).length : 0;
                          const hasOpenTasks = tab === 'tasks' && openTaskCount > 0;
                          return (
                            <button
                              key={tab}
                              onClick={() => setActiveTab(tab)}
                              className={`px-4 py-2 text-sm font-medium border-b-2 transition flex items-center gap-1.5 ${
                                activeTab === tab
                                  ? hasOpenTasks ? 'border-red-500 text-red-600' : 'border-emerald-600 text-emerald-600'
                                  : hasOpenTasks ? 'border-transparent text-red-500 hover:text-red-700' : 'border-transparent text-slate-600 hover:text-slate-900'
                              }`}
                            >
                              {tab === 'details' && 'Details'}
                              {tab === 'setup' && 'Event Setup'}
                              {tab === 'financials' && 'Financials'}
                              {tab === 'tasks' && 'Tasks'}
                              {tab === 'attachments' && 'Attachments'}
                              {tabMissBadge(missTabsE[tab])}
                              {hasOpenTasks && (
                                <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-red-500 text-white leading-none">{openTaskCount}</span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {/* Tab Content */}
                      {activeTab === 'details' && <MusicOnlyToggle data={editData} onChange={handleEditChange} className="mb-4" />}
                      {activeTab === 'details' && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Event Name *</label>
                              <input name="eventName" value={editData.eventName} onChange={handleEditChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missE('name')}`} />
                            </div>
                            <div className="relative">
                              <label className="block text-xs font-medium text-slate-500 mb-1">Client Name *</label>
                              <input
                                name="clientName"
                                value={editData.clientName}
                                onChange={(e) => handleClientNameChange(e, true)}
                                onBlur={() => setTimeout(() => setShowClientSuggestions(false), 200)}
                                className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missE('client')}`}
                                autoComplete="off"
                              />
                              {showClientSuggestions && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                                  {clientSuggestions.map((client) => (
                                    <button
                                      key={client.id}
                                      type="button"
                                      onClick={() => handleSelectClient(client, true)}
                                      className="w-full text-left px-3 py-2 hover:bg-emerald-50 text-sm"
                                    >
                                      <span className="font-medium">{client.name}</span>
                                      {client.company && <span className="text-slate-400 ml-2">{client.company}</span>}
                                      {client.tag && <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-xs">{client.tag}</span>}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Start Date *</label>
                              <input type="date" name="date" value={editData.date} onChange={handleEditChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missE('date')}`} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">End Date <span className="text-slate-400 font-normal">(multi-day)</span></label>
                              <input type="date" name="endDate" value={editData.endDate || ''} onChange={handleEditChange} min={editData.date || undefined} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Type</label>
                              <select name="type" value={editData.type} onChange={handleEditChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missE('type')}`}>
                                {EVENT_TYPE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </div>
                            {(editData.type || '').toLowerCase() === 'wedding' && (
                              <>
                                <div>
                                  <label className="block text-xs font-medium text-slate-500 mb-1">Partner 1</label>
                                  <input type="text" name="brideName" value={editData.brideName || ''} onChange={handleEditChange} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-slate-500 mb-1">Partner 2</label>
                                  <input type="text" name="groomName" value={editData.groomName || ''} onChange={handleEditChange} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                                </div>
                              </>
                            )}
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Subtype</label>
                              <select name="subtype" value={editData.subtype} onChange={handleEditChange} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                                <option value="">Select Subtype</option>
                                {EVENT_SUBTYPES.map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Venue *</label>
                              <select name="venue_id" value={editData.venue_id} onChange={handleEditChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missE('venue')}`}>
                                <option value="">Select Venue</option>
                                {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Guests</label>
                              <input type="number" name="guests" min="0" value={editData.guests} onChange={handleEditChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missE('guests')}`} />
                            </div>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Start Time</label>
                              <TimeSelect name="startTime" value={editData.startTime} onChange={handleEditChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missE('startTime')}`} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">End Time</label>
                              <TimeSelect name="endTime" value={editData.endTime} onChange={handleEditChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missE('endTime')}`} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Phone</label>
                              <input name="contactPhone" value={editData.contactPhone} onChange={handleEditChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missE('contact')}`} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
                              <input name="contactEmail" value={editData.contactEmail} onChange={handleEditChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missE('contact')}`} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Status</label>
                              <select name="stage" value={editData.stage} onChange={handleEditChange} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                                {[...getPipelineStages(editData.type), 'cancelled', 'archived', 'lost']
                                  .filter((st, i, arr) => arr.indexOf(st) === i && (st !== 'lost' || editData.stage === 'lost'))
                                  .concat(editData.stage && ![...getPipelineStages(editData.type), 'cancelled', 'archived', 'lost'].includes(editData.stage) ? [editData.stage] : [])
                                  .map((st) => <option key={st} value={st}>{STAGE_LABELS[st] || (st.charAt(0).toUpperCase() + st.slice(1))}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Notes</label>
                              <textarea name="notes" value={editData.notes} onChange={handleEditChange} rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
                            </div>
                          </div>
                        </div>
                      )}

                      {activeTab === 'setup' && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Setup Style *</label>
                              <select name="setup" value={editData.setup} onChange={handleEditChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missE('setup')}`}>
                                <option value="">Select setup</option>
                          {SETUP_STYLES.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Setup Requirements *</label>
                              <textarea name="setupRequirements" placeholder="Table setup: no. of tables, seats per table, top table, layout, linen &amp; extras (e.g. 8 rounds of 10, top table for 6, white linen, cake table)" value={editData.setupRequirements} onChange={handleEditChange} rows={3} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none${missE('setupRequirements')}`} />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Menu Notes</label>
                              <textarea name="menuNotes" value={editData.menuNotes} onChange={handleEditChange} rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
                            </div>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-2">Equipment</label>
                              <div className="flex flex-wrap gap-2">
                                {EQUIPMENT_OPTIONS.map((eq) => (
                                  <button
                                    key={eq}
                                    onClick={() => {
                                      const updated = editData.equipment.includes(eq)
                                        ? editData.equipment.filter((e) => e !== eq)
                                        : [...editData.equipment, eq];
                                      setEditData({ ...editData, equipment: updated });
                                    }}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                                      editData.equipment.includes(eq)
                                        ? 'bg-emerald-600 text-white'
                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                    }`}
                                  >
                                    {eq.replace(/_/g, ' ')}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-2">F&B Items</label>
                              <div className="flex flex-wrap gap-2 mb-2">
                                {FB_PRESETS.map((preset) => (
                                  <button
                                    key={preset.value}
                                    onClick={() => {
                                      const updated = editData.fbItems.includes(preset.value)
                                        ? editData.fbItems.filter((f) => f !== preset.value)
                                        : [...editData.fbItems, preset.value];
                                      setEditData({ ...editData, fbItems: updated });
                                    }}
                                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                                      editData.fbItems.includes(preset.value)
                                        ? 'bg-emerald-600 text-white'
                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                    }`}
                                  >
                                    {preset.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-slate-500 mb-1">Dietary Requirements</label>
                              <textarea name="dietaryRequirements" value={editData.dietaryRequirements} onChange={handleEditChange} rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none" />
                              <KitchenFields data={editData} onChange={handleEditChange} />
                            </div>
                          </div>
                        </div>
                      )}

                      {activeTab === 'financials' && (
                        <div className="space-y-4 mb-6">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <div className="space-y-3">
                              <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Price Per Head (€)</label>
                                <input type="number" name="pricePerHead" value={editData.pricePerHead || ''} onChange={(e) => {
                                  const pph = parseFloat(e.target.value) || 0;
                                  const autoTotal = pph * (editData.guests || 0);
                                  setEditData({ ...editData, pricePerHead: pph, packageTotal: autoTotal });
                                }} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missE('pricing')}`} />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Package Total (€)</label>
                                <input type="number" name="packageTotal" value={editData.packageTotal} onChange={handleEditChange} className={`w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500${missE('pricing')}`} />
                                {editData.pricePerHead > 0 && editData.guests > 0 && (
                                  <p className="text-xs text-slate-400 mt-1">{fmt(editData.pricePerHead)} × {editData.guests} guests</p>
                                )}
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Room Hire (€)</label>
                                <input type="number" name="roomHire" value={editData.roomHire} onChange={handleEditChange} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-slate-500 mb-1">Billing Type</label>
                                <select name="billingType" value={editData.billingType} onChange={handleEditChange} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                                  <option value="group">Group</option>
                                  <option value="individual">Individual</option>
                                  <option value="per_head">Per Head</option>
                                </select>
                              </div>
                            </div>
                            <div className="space-y-3">
                              {(() => {
                                const evtPayments = (payments || []).filter((p) => p.event_id === event.id);
                                const totalPaid = evtPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
                                const totalVal = (event.packageTotal || 0) + (event.roomHire || 0);
                                const outstanding = totalVal - totalPaid;
                                return (
                                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                                    <h4 className="text-sm font-semibold text-slate-900 mb-3">Financial Summary</h4>
                                    <div className="grid grid-cols-3 gap-3 text-center">
                                      <div>
                                        <p className="text-xs text-slate-500">Total</p>
                                        <p className="text-base font-bold text-slate-900">{fmt(totalVal)}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-slate-500">Paid</p>
                                        <p className="text-base font-bold text-emerald-700">{fmt(totalPaid)}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-slate-500">Outstanding</p>
                                        <p className={`text-base font-bold ${outstanding > 0 ? 'text-red-600' : 'text-emerald-700'}`}>{fmt(outstanding)}</p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>

                          {/* Record Payment */}
                          <div>
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                                <CreditCard size={16} />
                                Payments
                              </h4>
                              <button
                                onClick={() => setShowPaymentForm(!showPaymentForm)}
                                className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-white transition"
                                style={{ backgroundColor: '#1A5C3A' }}
                              >
                                <Plus size={12} />
                                Record
                              </button>
                            </div>
                            {showPaymentForm && (
                              <div className="mb-3 bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                                <div className="grid grid-cols-2 gap-2">
                                  <input type="number" step="0.01" value={paymentFormData.amount} onChange={(e) => setPaymentFormData({ ...paymentFormData, amount: e.target.value })} placeholder="Amount €" className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                                  <input type="date" value={paymentFormData.payment_date} onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_date: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
                                  <select value={paymentFormData.paymentType} onChange={(e) => setPaymentFormData({ ...paymentFormData, paymentType: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                                    <option value="bank_transfer">Bank Transfer</option>
                                    <option value="card">Card</option>
                                    <option value="cash">Cash</option>
                                    <option value="cheque">Cheque</option>
                                  </select>
                                  <select value={paymentFormData.category} onChange={(e) => setPaymentFormData({ ...paymentFormData, category: e.target.value })} className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500">
                                    <option value="deposit">Deposit</option>
                                    <option value="room_hire">Room Hire</option>
                                    <option value="fb_package">F&B / Package</option>
                                    <option value="final_balance">Final Balance</option>
                                    <option value="other">Other</option>
                                  </select>
                                  <input type="text" value={paymentFormData.description} onChange={(e) => setPaymentFormData({ ...paymentFormData, description: e.target.value })} placeholder="Description" className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 col-span-2" />
                                </div>
                                <div className="flex gap-2 mt-2">
                                  <button onClick={() => handleRecordPayment(event.id)} className="flex-1 py-2 rounded-lg text-sm font-medium text-white transition" style={{ backgroundColor: '#1A5C3A' }}>Save</button>
                                  <button onClick={() => setShowPaymentForm(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 bg-slate-200 hover:bg-slate-300 transition">Cancel</button>
                                </div>
                              </div>
                            )}
                            {(() => {
                              const evtPayments = (payments || []).filter((p) => p.event_id === event.id);
                              return evtPayments.length > 0 ? (
                                <div className="space-y-1.5">
                                  {evtPayments.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date)).map((payment) => (
                                    <div key={payment.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border border-slate-200 group">
                                      <div className="min-w-0">
                                        <p className="text-sm font-medium text-slate-900 truncate">{payment.description || 'Payment'}</p>
                                        <p className="text-xs text-slate-500">
                                          {fmtDate(payment.payment_date)} • {(payment.paymentType || 'bank_transfer').replace(/_/g, ' ')}
                                          {payment.category && payment.category !== 'package' && (
                                            <span className="ml-1 inline-block px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px] font-medium uppercase">{payment.category.replace(/_/g, ' ')}</span>
                                          )}
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        <span className="text-sm font-bold text-emerald-700">{fmt(payment.amount)}</span>
                                        <button
                                          type="button"
                                          onClick={() => handleDeletePayment(payment)}
                                          title="Delete this payment (e.g. added by mistake)"
                                          className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition"
                                          aria-label="Delete payment"
                                        >
                                          <Trash2 size={14} />
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-slate-500 italic">No payments yet</p>
                              );
                            })()}
                          </div>
                        </div>
                      )}

                      {activeTab === 'tasks' && (
                        <div className="mb-6">
                          <EventTasks eventId={event.id} />
                          <EventHistory eventId={event.id} />
                        </div>
                      )}

                      {activeTab === 'attachments' && (
                        <div className="space-y-4 mb-6">
                          <div>
                            <input ref={fileInputRef} type="file" multiple onChange={(e) => handleFileUpload(event.id, e.target.files)} className="hidden" />
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              disabled={uploadingAttachment}
                              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-50"
                            >
                              <Upload size={16} />
                              {uploadingAttachment ? 'Uploading...' : 'Upload File'}
                            </button>
                          </div>
                          {(() => {
                            const attachments = editData.attachments || (events.find(e => e.id === event.id)?.attachments || []);
                            return attachments.length > 0 ? (
                              <div className="space-y-2">
                                {attachments.map((att) => (
                                  <div key={att.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                                    <div className="flex items-center gap-2 min-w-0">
                                      <File size={16} className="text-slate-400 flex-shrink-0" />
                                      <div className="min-w-0">
                                        <p className="text-sm font-medium text-slate-900 truncate">{att.name}</p>
                                        <p className="text-xs text-slate-500">{formatFileSize(att.size)}</p>
                                      </div>
                                    </div>
                                    <div className="flex gap-2 flex-shrink-0">
                                      <button
                                        onClick={() => handleDownloadAttachment(att)}
                                        className="p-1 text-blue-600 hover:bg-blue-50 rounded transition"
                                      >
                                        <Download size={16} />
                                      </button>
                                      <button
                                        onClick={() => handleDeleteAttachment(event.id, att)}
                                        className="p-1 text-red-600 hover:bg-red-50 rounded transition"
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-xs text-slate-500 italic">No attachments yet</p>
                            );
                          })()}
                        </div>
                      )}

                      {/* Wedding details & checklist (moved here from the retired Wedding Pipeline page) */}
                      {(event.type || '').toLowerCase() === 'wedding' && (
                        <details className="mt-6 border border-pink-200 rounded-lg bg-pink-50/40">
                          <summary className="cursor-pointer select-none px-4 py-2 text-sm font-semibold text-pink-800">💒 Wedding details &amp; checklist</summary>
                          <div className="p-3">
                            <WeddingChecklist event={event} onSave={(weddingDetails) => updateEvent(event.id, { weddingDetails })} />
                          </div>
                        </details>
                      )}
                      {(event.type || '').toLowerCase() === 'wedding' && (
                        <details className="mt-3 border border-pink-200 rounded-lg bg-pink-50/40">
                          <summary className="cursor-pointer select-none px-4 py-2 text-sm font-semibold text-pink-800">📝 Contract</summary>
                          <div className="p-3">
                            <ContractSigning
                              event={event}
                              onUpdate={(fields) => updateEvent(event.id, fields)}
                              onSendEmail={(emailData) => addSentEmail?.({ eventId: event.id, to: emailData.to, subject: emailData.subject, body: emailData.body, sentAt: new Date().toISOString(), type: 'contract' })}
                            />
                          </div>
                        </details>
                      )}

                      <div className="mt-6">
                        <DetailStatusPanel event={editData} onGoToTab={setActiveTab} />
                      </div>

                      {/* Action Buttons */}
                      <div className="mt-4 pt-4 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                          <button
                            onClick={handleDeleteEvent}
                            className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition min-h-[44px] sm:min-h-auto"
                          >
                            <Trash2 size={16} />
                            Delete
                          </button>
                          <button
                            onClick={() => handleCloneEvent(event)}
                            className="flex items-center gap-2 px-4 py-2 text-indigo-700 hover:bg-indigo-50 rounded-lg text-sm font-medium border border-indigo-200 transition min-h-[44px] sm:min-h-auto"
                          >
                            <Copy size={16} />
                            Clone
                          </button>
                          <button
                            onClick={() => onSelectEventInPage ? onSelectEventInPage(event, 'functionsheets') : onNavigate?.('functionsheets')}
                            className="flex items-center gap-2 px-4 py-2 text-blue-700 hover:bg-blue-50 rounded-lg text-sm font-medium border border-blue-200 transition min-h-[44px] sm:min-h-auto"
                          >
                            <FileText size={16} />
                            <span className="hidden md:inline">Function Sheet</span>
                            <span className="md:hidden">Sheet</span>
                          </button>
                          {canSendEventToRestaurant(event) && (
                            <button
                              onClick={() => handleOpenSendToRestaurant(event)}
                              className="flex items-center gap-2 px-4 py-2 text-purple-700 hover:bg-purple-50 rounded-lg text-sm font-medium border border-purple-200 transition min-h-[44px] sm:min-h-auto"
                              title="Create a matching table booking on the Tables page"
                            >
                              <Utensils size={16} />
                              <span className="hidden md:inline">Send to Restaurant</span>
                              <span className="md:hidden">To Restaurant</span>
                            </button>
                          )}
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            onClick={handleCollapseCard}
                            className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium transition min-h-[44px] sm:min-h-auto"
                          >
                            Collapse
                          </button>
                          <button
                            onClick={handleSaveEditedEvent}
                            className="flex items-center gap-2 px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition min-h-[44px] sm:min-h-auto"
                          >
                            <Save size={16} />
                            Save Changes
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Email Composer Modal */}
      {showEmailComposer && (
        <EmailComposer
          isOpen={showEmailComposer}
          onClose={() => { setShowEmailComposer(false); setComposerEvent(null); }}
          events={events}
          venues={venues}
          emailTemplates={emailTemplates}
          onEmailSent={addSentEmail}
          preselectedEvent={composerEvent}
        />
      )}

      {/* Send to Restaurant Modal */}
      {showSendToRestaurant && sendToRestaurantEvent && (() => {
        const evt = sendToRestaurantEvent;
        const validSittings = getSittingsForVenueAndDate(sendToRestaurantData.venue, evt.date);
        const currentSittingDef = RESTAURANT_SITTINGS.find((s) => s.id === sendToRestaurantData.sitting);
        const venueCap = RESTAURANT_VENUES.find((v) => v.id === sendToRestaurantData.venue)?.capacity || 60;
        const minCovers = getMinCoversForRestaurant(sendToRestaurantData.venue);
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={handleCloseSendToRestaurant}>
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b border-slate-200">
                <div className="flex items-center gap-2">
                  <Utensils size={20} className="text-purple-600" />
                  <h2 className="text-lg font-semibold text-slate-900">Send to Restaurant</h2>
                </div>
                <button onClick={handleCloseSendToRestaurant} className="p-1 hover:bg-slate-100 rounded">
                  <X size={18} className="text-slate-500" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                  <div className="font-medium text-slate-900">{evt.name}</div>
                  <div className="text-slate-600 mt-1 text-xs">
                    {evt.client} · {fmtDate(evt.date)}{evt.startTime ? ` · ${evt.startTime}` : ''} · {evt.guests || 0} guests
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Restaurant</label>
                  <select
                    value={sendToRestaurantData.venue}
                    onChange={(e) => {
                      const newVenue = e.target.value;
                      const newValid = getSittingsForVenueAndDate(newVenue, evt.date);
                      const newSitting = newValid.some((s) => s.id === sendToRestaurantData.sitting)
                        ? sendToRestaurantData.sitting
                        : (newValid[0]?.id || '');
                      setSendToRestaurantData((prev) => ({ ...prev, venue: newVenue, sitting: newSitting }));
                    }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    {RESTAURANT_VENUES.map((v) => (
                      <option key={v.id} value={v.id}>{v.name} (max {v.capacity})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Sitting</label>
                  {validSittings.length === 0 ? (
                    <p className="text-sm text-red-600">This restaurant isn't open on {fmtDate(evt.date)}. Pick a different one.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2">
                      {RESTAURANT_SITTINGS.map((s) => {
                        const disabled = !validSittings.some((v) => v.id === s.id);
                        const selected = sendToRestaurantData.sitting === s.id;
                        return (
                          <button
                            key={s.id}
                            type="button"
                            disabled={disabled}
                            onClick={() => {
                              const newTime = `${String(s.startH).padStart(2, '0')}:00`;
                              setSendToRestaurantData((prev) => ({ ...prev, sitting: s.id, time: newTime }));
                            }}
                            className={`px-3 py-2 rounded-lg border text-xs font-medium transition ${
                              disabled
                                ? 'bg-slate-50 border-slate-200 text-slate-300 cursor-not-allowed'
                                : selected
                                ? 'bg-purple-600 border-purple-600 text-white'
                                : 'bg-white border-slate-300 text-slate-700 hover:border-purple-400'
                            }`}
                          >
                            <div>{s.label}</div>
                            <div className="text-[10px] opacity-70 mt-0.5">{s.time}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Time</label>
                    <TimeSelect
                      value={sendToRestaurantData.time}
                      onChange={(e) => setSendToRestaurantData((prev) => ({ ...prev, time: e.target.value }))}
                      startHour={currentSittingDef ? currentSittingDef.startH : 6}
                      endHour={currentSittingDef ? currentSittingDef.endH - 1 : 23}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    {currentSittingDef && (
                      <p className="text-[10px] text-slate-400 mt-1">Within {currentSittingDef.time}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Covers</label>
                    <input
                      type="number"
                      min={minCovers}
                      max={venueCap}
                      value={sendToRestaurantData.covers}
                      onChange={(e) => setSendToRestaurantData((prev) => ({ ...prev, covers: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-[10px] text-slate-400 mt-1">
                      {minCovers > 1 ? `Min ${minCovers} · ` : ''}Max {venueCap}
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
                  A new table booking will be created on the Tables page and linked to this event. You can remove it from either side.
                </div>
              </div>

              <div className="flex justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
                <button
                  onClick={handleCloseSendToRestaurant}
                  disabled={sendToRestaurantBusy}
                  className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSendToRestaurant}
                  disabled={sendToRestaurantBusy || validSittings.length === 0}
                  className="flex items-center gap-2 px-5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  <Utensils size={14} />
                  {sendToRestaurantBusy ? 'Sending…' : 'Send to Restaurant'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}